import { infiniteQueryOptions, useInfiniteQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'

import { useInterval } from '@/src/general/hooks/useInterval'
import { sectionizeInfiniteData } from '@/src/general/utils/sectionizeInfiniteData'

import { messageApi } from '../api/messageApi'

export const GET_MESSAGES_LIMIT = 10
export const MESSAGES_POLL_INTERVAL = 15000

const removeEmptyPages = (queryClient: QueryClient, conversationId: number) =>
  queryClient.setQueryData(getConversationMessagesOptions(conversationId).queryKey, (data) => {
    if (!data) return data
    const newData: typeof data = { pages: [], pageParams: [] }
    for (let i = 0; i < data.pages.length; i++) {
      if (data.pages[i].length > 0) {
        newData.pages.push(data.pages[i])
        newData.pageParams.push(data.pageParams[i])
      }
    }
    return newData
  })

export const getConversationMessagesOptions = (conversationId?: number) =>
  infiniteQueryOptions({
    queryKey: ['conversation', conversationId, 'messages'],
    queryFn: ({ signal, pageParam }) =>
      // oxlint-disable-next-line @typescript-eslint/no-non-null-assertion -- the query is enabled only if conversationId is defined
      messageApi.getConversationMessages({ conversationId: conversationId!, ...pageParam }, { signal }),
    retry: 3,
    // If staleTime is set to 0, some messages are lost when navigating back and forth between conversations
    staleTime: 5 * 60 * 1000,
    initialPageParam: { limit: GET_MESSAGES_LIMIT },
    getNextPageParam: (lastPage, _pages, lastPageParams) =>
      lastPage?.length > 0 ? { afterId: lastPage[0].id, limit: GET_MESSAGES_LIMIT } : lastPageParams,
    getPreviousPageParam: (firstPage) =>
      firstPage?.length === GET_MESSAGES_LIMIT
        ? { timestamp: new Date(firstPage[firstPage.length - 1].createdAt).valueOf(), limit: GET_MESSAGES_LIMIT }
        : null,
    select: ({ pages }) => {
      const flatData = [...pages].reverse().flat()
      return sectionizeInfiniteData(flatData, (item) => item.createdAt)
    },
    enabled: !!conversationId,
  })

export const useConversationMessagesQuery = (conversationId: number) => {
  const queryClient = useQueryClient()
  const query = useInfiniteQuery(getConversationMessagesOptions(conversationId))

  const fetchNextPage = async () => {
    await query.fetchNextPage()
    // fetchNextPage creates a new empty page if there are no new messages
    removeEmptyPages(queryClient, conversationId)
  }

  // Fetch new messages every 15 seconds
  // This is a temporary solution until we implement websockets
  // Using the refetch prop in useQuery would refetch all the conversation messages
  useInterval(async () => {
    await fetchNextPage()
  }, MESSAGES_POLL_INTERVAL)

  return { ...query, fetchNextPage }
}
